import { AsyncThunk } from './types';
import { GetVersionModel } from './api/model';
import {
  ChatKey,
  type DayMessageMap,
  getChatKey,
  getChatKeyFromRoute,
} from './util/getChatKey';

import { DayMessage, UserKey } from '@pdcplan/shared/api/Message';
import { MsgVisibilityOldEnum } from '@pdcplan/shared/enums';
import { Dispatch } from 'redux';
import {
  TeamPlanSettings,
  TeamShiftDef,
  UserSettings,
} from '@pdcplan/shared/api/TeamPlan';
import { ModalWrapperProps } from './Modal/ModalWrapper';
import getApi from './getApi';
import { Holiday } from '@pdcplan/shared/api/Calendar';
import { ActionType } from './ActionType';
import { ConfirmDialogProps } from './components/ConfirmDialog/ConfirmDialog';
import { InfoDialogProps } from './components/InfoDialog/InfoDialog';
import { GlobalSettings } from '@pdcplan/shared/api/Common';
import { Location } from 'react-router-dom';
import { Period } from './util/dates';
import { currentLanguage } from './currentLanguage';
import { ToastType, ToastVariant } from '@pdcfrontendui/components';
import { getRouteState } from './routes';
import { history } from './history';

export type AppAction =
  | {
      type: ActionType.TOGGLE_SCREEN_SMALL;
    }
  | {
      type: ActionType.TOGGLE_SCREEN_BIG;
    }
  | {
      type: ActionType.CHANGE_DATE;
      date: Date;
    }
  | {
      type: ActionType.TOGGLE_CALENDAR;
    }
  | {
      type: ActionType.TOGGLE_SEARCHBAR;
    }
  | {
      type: ActionType.SET_MODALLOADING;
      modalLoading: boolean;
    }
  | {
      type: ActionType.TOGGLE_MENU;
    }
  | {
      type: ActionType.GETVERSION_REQUEST;
    }
  | {
      type: ActionType.GETVERSION_SUCCESS;
      getVersionResponse: GetVersionModel;
    }
  | {
      type: ActionType.GETVERSION_FAILURE;
      errorMessage: string;
    }
  | {
      type: ActionType.SET_VERSION;
      version: GetVersionModel;
    }
  | {
      type: ActionType.GETGLOBALSETTINGS_REQUEST;
    }
  | {
      type: ActionType.GETGLOBALSETTINGS_SUCCESS;
      globalSettings: GlobalSettings;
    }
  | {
      type: ActionType.GETGLOBALSETTINGS_FAILURE;
      errorMessage: string;
    }
  | {
      type: ActionType.GETSETTINGS_REQUEST;
    }
  | {
      type: ActionType.GETSETTINGS_SUCCESS;
      settings: TeamPlanSettings;
    }
  | {
      type: ActionType.GETSETTINGS_FAILURE;
      errorMessage: string;
    }
  | {
      type: ActionType.GETUSERSETTINGS_REQUEST;
    }
  | {
      type: ActionType.GETUSERSETTINGS_SUCCESS;
      userSettings: UserSettings;
    }
  | {
      type: ActionType.GETUSERSETTINGS_FAILURE;
      errorMessage: string;
    }
  | {
      type: ActionType.OPEN_MODAL;
      modal: ModalWrapperProps;
    }
  | {
      type: ActionType.CLOSE_MODAL;
    }
  | {
      type: ActionType.SET_APP_LOADING;
      appLoading: boolean;
    }
  | {
      type: ActionType.GETMESSAGES_SUCCESS;
      dayMessageMap: DayMessageMap;
    }
  | {
      type: ActionType.POSTMESSAGE_REQUEST;
      tempMessage: DayMessage;
    }
  | {
      type: ActionType.POSTMESSAGE_SUCCESS;
      tempMessage: DayMessage;
      message: DayMessage;
    }
  | { type: ActionType.POSTMESSAGE_FAILURE; tempMessage: DayMessage }
  | {
      type: ActionType.GETUNREADMESSAGES_SUCCESS;
      unreadCountMap: Record<string, number>;
    }
  | {
      type: ActionType.GOT_HOLIDAYS;
      holidays: Array<Holiday>;
    }
  | {
      type: ActionType.QUEUE_TOAST;
      toast: ToastType;
    }
  | {
      type: ActionType.SHOW_CONFIRM_DIALOG;
      payload: Omit<ConfirmDialogProps, 'shown'>;
    }
  | {
      type: ActionType.HIDE_CONFIRM_DIALOG;
    }
  | {
      type: ActionType.SHOW_INFO_DIALOG;
      payload: Omit<InfoDialogProps, 'shown'>;
    }
  | {
      type: ActionType.HIDE_INFO_DIALOG;
    }
  | {
      type: ActionType.SHOW_ERROR_MODAL;
      payload: {
        error: unknown;
        title?: string;
        isConnectionError?: boolean;
      };
    }
  | {
      type: ActionType.HIDE_ERROR_MODAL;
    }
  | { type: ActionType.LOCATION_CHANGE; payload: Location }
  | { type: ActionType.SET_SHOWING_RULE_VIOLATION_INFO; payload: boolean }
  | { type: ActionType.SET_SHIFTDEFMAP; data: TeamShiftDef[] }
  | {
      type: ActionType.ADD_FAILED_POLLING_ENDPOINT;
      payload: string;
    }
  | {
      type: ActionType.REMOVE_FAILED_POLLING_ENDPOINT;
      payload: string;
    }
  | {
      type: ActionType.SET_HAS_INTERNET;
      payload: boolean;
    }
  | {
      type: ActionType.SET_CONNECTION_ERROR;
      payload: Error | null;
    }
  | {
      type: ActionType.SHOW_NETWORK_MODAL;
      payload?: Error | null;
    }
  | { type: ActionType.HIDE_NETWORK_MODAL }
  | { type: ActionType.MARK_THREAD_AS_READ; payload: ChatKey };

export const toggleScreenSmall = (): AppAction => ({
  type: ActionType.TOGGLE_SCREEN_SMALL,
});

export const toggleScreenBig = (): AppAction => ({
  type: ActionType.TOGGLE_SCREEN_BIG,
});

export const changeDate = (date: Date): AppAction => ({
  type: ActionType.CHANGE_DATE,
  date,
});

export const toggleCalendar = (): AppAction => ({
  type: ActionType.TOGGLE_CALENDAR,
});
export const toggleSearchBar = (): AppAction => ({
  type: ActionType.TOGGLE_SEARCHBAR,
});

export const toggleMenu = (): AppAction => ({
  type: ActionType.TOGGLE_MENU,
});

export const setModalLoading = (modalLoading: boolean): AppAction => ({
  type: ActionType.SET_MODALLOADING,
  modalLoading,
});

export const openModal = (modal: ModalWrapperProps): AppAction => ({
  type: ActionType.OPEN_MODAL,
  modal,
});

export const closeModal = (): AppAction => ({
  type: ActionType.CLOSE_MODAL,
});

export const getVersionRequest = (): AppAction => ({
  type: ActionType.GETVERSION_REQUEST,
});

export const getVersionSuccess = (
  getVersionResponse: GetVersionModel
): AppAction => ({
  type: ActionType.GETVERSION_SUCCESS,
  getVersionResponse,
});

export const getVersionFailure = (errorMessage: string): AppAction => ({
  type: ActionType.GETVERSION_FAILURE,
  errorMessage,
});
export const setVersion = (version: GetVersionModel): AppAction => ({
  type: ActionType.SET_VERSION,
  version,
});
export const getGlobalSettingsRequest = (): AppAction => ({
  type: ActionType.GETGLOBALSETTINGS_REQUEST,
});

export const getGlobalSettingsSuccess = (
  globalSettings: GlobalSettings
): AppAction => ({
  type: ActionType.GETGLOBALSETTINGS_SUCCESS,
  globalSettings,
});

export const getGlobalSettingsFailure = (errorMessage: string): AppAction => ({
  type: ActionType.GETGLOBALSETTINGS_FAILURE,
  errorMessage,
});

export const getSettingsRequest = (): AppAction => ({
  type: ActionType.GETSETTINGS_REQUEST,
});

export const getSettingsSuccess = (settings: TeamPlanSettings): AppAction => ({
  type: ActionType.GETSETTINGS_SUCCESS,
  settings,
});

export const getSettingsFailure = (errorMessage: string): AppAction => ({
  type: ActionType.GETSETTINGS_FAILURE,
  errorMessage,
});

export const getUserSettingsRequest = (): AppAction => ({
  type: ActionType.GETUSERSETTINGS_REQUEST,
});

export const getUserSettingsSuccess = (
  userSettings: UserSettings
): AppAction => ({
  type: ActionType.GETUSERSETTINGS_SUCCESS,
  userSettings,
});

export const getUserSettingsFailure = (errorMessage: string): AppAction => ({
  type: ActionType.GETUSERSETTINGS_FAILURE,
  errorMessage,
});

export const getMessagesSuccess = (
  dayMessageMap: DayMessageMap
): AppAction => ({
  type: ActionType.GETMESSAGES_SUCCESS,
  dayMessageMap,
});

export const postMessageRequest = (tempMessage: DayMessage): AppAction => ({
  type: ActionType.POSTMESSAGE_REQUEST,
  tempMessage,
});

export const postMessageSuccess = (
  tempMessage: DayMessage,
  message: DayMessage
): AppAction => ({
  type: ActionType.POSTMESSAGE_SUCCESS,
  tempMessage,
  message,
});

export const postMessageFailure = (tempMessage: DayMessage): AppAction => ({
  type: ActionType.POSTMESSAGE_FAILURE,
  tempMessage,
});

export const getUnreadMessagesCountSuccess = (
  unreadCountMap: Record<string, number>
): AppAction => ({
  type: ActionType.GETUNREADMESSAGES_SUCCESS,
  unreadCountMap,
});

export const gotHolidays = (holidays: Holiday[]): AppAction => ({
  type: ActionType.GOT_HOLIDAYS,
  holidays,
});

export const queueToast = (toast: ToastType): AppAction => ({
  type: ActionType.QUEUE_TOAST,
  toast,
});

export const showConfirmDialog = (
  payload: Omit<ConfirmDialogProps, 'shown'>
): AppAction => ({
  type: ActionType.SHOW_CONFIRM_DIALOG,
  payload,
});

export const hideConfirmDialog = (): AppAction => ({
  type: ActionType.HIDE_CONFIRM_DIALOG,
});

export const showInfoDialog = (
  payload: Omit<InfoDialogProps, 'shown'>
): AppAction => ({
  type: ActionType.SHOW_INFO_DIALOG,
  payload,
});

export const hideInfoDialog = (): AppAction => ({
  type: ActionType.HIDE_INFO_DIALOG,
});

export const showErrorModal = (
  error: unknown,
  title?: string,
  isConnectionError?: boolean
): AppAction => ({
  type: ActionType.SHOW_ERROR_MODAL,
  payload: { error, title, isConnectionError: isConnectionError || false },
});

export const hideErrorModal = (): AppAction => ({
  type: ActionType.HIDE_ERROR_MODAL,
});

export const setShiftDefs = (data: TeamShiftDef[]): AppAction => ({
  type: ActionType.SET_SHIFTDEFMAP,
  data,
});

export const setHasInternet = (payload: boolean): AppAction => ({
  type: ActionType.SET_HAS_INTERNET,
  payload,
});

export const setConnectionError = (payload: Error | null): AppAction => ({
  type: ActionType.SET_CONNECTION_ERROR,
  payload,
});

export const addFailedPollingEndpoint = (endpoint: string) => ({
  type: ActionType.ADD_FAILED_POLLING_ENDPOINT,
  payload: endpoint,
});

export const removeFailedPollingEndpoint = (endpoint: string) => ({
  type: ActionType.REMOVE_FAILED_POLLING_ENDPOINT,
  payload: endpoint,
});

export function attemptGetVersion() {
  return (dispatch: Dispatch) => {
    dispatch(getVersionRequest());
    return getApi()
      .getVersion()
      .then((systemInformation) => {
        const version: GetVersionModel = {
          ...systemInformation,
          serviceUrl: getApi().baseUrl,
          appVersion: import.meta.env.VITE_TEAMPLAN_VERSION,
        };
        dispatch(getVersionSuccess(version));
        dispatch(setConnectionError(null));
        void dispatch(attemptGetGlobalSettings());
      })
      .catch((err: Error) => {
        dispatch(getVersionFailure(err.message));
        dispatch(setConnectionError(err));
        const frontendVersion: GetVersionModel = {
          serviceUrl: getApi().baseUrl,
          appVersion: import.meta.env.VITE_TEAMPLAN_VERSION,
          appVersionMin: '',
          dbVersion: '',
          serviceVersion: '',
        };
        dispatch(setVersion(frontendVersion));
      });
  };
}
export function attemptGetGlobalSettings() {
  return (dispatch: Dispatch) => {
    dispatch(getGlobalSettingsRequest());
    return getApi()
      .getGlobalSettings()
      .then((data) => {
        dispatch(getGlobalSettingsSuccess(data));
        dispatch(setConnectionError(null));
      })
      .catch((err: Error) => {
        dispatch(setConnectionError(err));
        dispatch(getGlobalSettingsFailure(err.message));
      });
  };
}

export function attemptGetSettings() {
  return (dispatch: Dispatch) => {
    dispatch(getSettingsRequest());
    return getApi()
      .getSettings()
      .then((data) => {
        dispatch(getSettingsSuccess(data));
      })
      .catch((err) => {
        dispatch(getSettingsFailure(err.message));
      });
  };
}

export function attemptGetUserSettings() {
  return (dispatch: Dispatch) => {
    dispatch(getUserSettingsRequest());
    return getApi()
      .getUserSettings()
      .then((data) => {
        dispatch(getUserSettingsSuccess(data));
      })
      .catch((err) => {
        dispatch(getUserSettingsFailure(err.message));
      });
  };
}

export const getHolidays = () => {
  return async (dispatch: Dispatch) => {
    getApi()
      .getHolidays()
      .then((response) => dispatch(gotHolidays(response.Holidays)))
      .catch((err) => {});
  };
};

export function attemptGetMessages(
  personIds: number[],
  from: Date,
  to: Date,
  isPoll: boolean
): AsyncThunk {
  return async (dispatch, getState) => {
    try {
      let dayMessages = await getApi().getMessages(personIds, from, to, isPoll);
      // Important that we get the chat key after the request
      const chatKey = getChatKeyFromRoute(
        getRouteState(history.location),
        getState().listViewReducer.shiftMap
      );
      const unreadIdsInCurrentChat = new Set(
        chatKey &&
          getState()
            .appReducer.dayMessageMap[chatKey]?.filter(
              (message) => !message.ReadWhen
            )
            .map((message) => message.MessageId)
      );
      dayMessages = dayMessages.map((dayMessage) =>
        unreadIdsInCurrentChat.has(dayMessage.MessageId)
          ? {
              ...dayMessage,
              ReadWhen: null,
            }
          : dayMessage
      );
      dayMessages.sort((a, b) => a.MessageId - b.MessageId); // MessageId gives the most precise order
      const messageMap = dayMessages.reduce<DayMessageMap>((acc, curr) => {
        const key = getChatKey(curr.PersonId, curr.Day, curr.Visibility);
        if (acc[key]) {
          acc[key].push(curr);
        } else {
          acc[key] = [curr];
        }
        return acc;
      }, {});

      if (
        isPoll &&
        getState().appReducer.failedPollingEndpoints.includes('getMessages')
      ) {
        dispatch(
          queueToast({
            text: currentLanguage.PollingSuccessMessages,
            variant: ToastVariant.Success,
          })
        );
        dispatch(removeFailedPollingEndpoint('getMessages'));
      }

      dispatch(getMessagesSuccess(messageMap));
    } catch (err) {
      if (
        isPoll &&
        !getState().appReducer.failedPollingEndpoints.includes('getMessages')
      ) {
        dispatch(
          queueToast({
            text: currentLanguage.PollingFailMessages,
            variant: ToastVariant.Error,
          })
        );
      }
      dispatch(addFailedPollingEndpoint('getMessages'));
    }
  };
}

export function attemptPostMessage(
  personId: number,
  messageText: string,
  isInternalMessage: boolean,
  day: Date,
  sentWhen: Date
): AsyncThunk {
  return async (dispatch, getState) => {
    const visibility = isInternalMessage
      ? MsgVisibilityOldEnum.planner
      : MsgVisibilityOldEnum.employee;
    const existingMessages =
      getState().appReducer.dayMessageMap[
        getChatKey(personId, day, visibility)
      ];
    const tempMessage = new DayMessage(
      existingMessages?.length
        ? Math.max(...existingMessages.map((msg) => msg.MessageId)) + 1
        : 1,
      messageText,
      personId,
      day,
      getState().loginReducer.fullName,
      new UserKey(
        getState().loginReducer.userId,
        getState().loginReducer.userType
      ),
      sentWhen,
      visibility,
      null
    );
    try {
      // Optimistically add the message to the state so user sees it immediately
      dispatch(postMessageRequest(tempMessage));
      const actualMessage = await getApi().postMessage(
        day,
        personId,
        messageText,
        isInternalMessage
      );
      // If the API call was successful, we can update the state with the actual message
      dispatch(postMessageSuccess(tempMessage, actualMessage));
    } catch (err) {
      // If the API call failed, we need to remove the temp message from the state
      dispatch(postMessageFailure(tempMessage));
    }
  };
}

/**
 * Marks messages as read with an API call, but does not update any state.
 */
export function attemptMarkMessagesAsRead(
  messageIds: number[]
): AsyncThunk<number[]> {
  return async () => {
    return await getApi().markMessagesAsRead(messageIds);
  };
}

export function atteptGetUnreadMessagesCount(
  teamIds: string[],
  from: Date,
  to: Date,
  isPoll: boolean
): AsyncThunk {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      if (teamIds.length > 0) {
        const unreadCounts = await getApi().getUnreadMessagesCount(
          teamIds,
          from,
          to,
          isPoll
        );
        const unreadCountMap = unreadCounts.reduce<Record<string, number>>(
          (acc, curr) => {
            acc[curr.TeamId] = curr.NumberOfUnreadMessages;
            return acc;
          },
          {}
        );

        if (
          isPoll &&
          state.appReducer.failedPollingEndpoints.includes(
            'getUnreadMessagesCount'
          )
        ) {
          dispatch(
            queueToast({
              text: currentLanguage.PollingSuccessUnreadMessageCount,
              variant: ToastVariant.Success,
            })
          );
          dispatch(removeFailedPollingEndpoint('getUnreadMessagesCount'));
        }

        dispatch(getUnreadMessagesCountSuccess(unreadCountMap));
      }
    } catch (err) {
      if (
        isPoll &&
        !state.appReducer.failedPollingEndpoints.includes(
          'getUnreadMessagesCount'
        )
      ) {
        dispatch(
          queueToast({
            text: currentLanguage.PollingFailUnreadMessageCount,
            variant: ToastVariant.Error,
          })
        );
      }
      dispatch(addFailedPollingEndpoint('getUnreadMessagesCount'));
    }
  };
}

export function setLocationForTracking(location: Location): AppAction {
  return {
    type: ActionType.LOCATION_CHANGE,
    payload: location,
  };
}

export function setShowingRuleViolationInfo(
  setShowingInfo: boolean
): AppAction {
  return {
    type: ActionType.SET_SHOWING_RULE_VIOLATION_INFO,
    payload: setShowingInfo,
  };
}

export function attemptGetShiftDefs(
  teamId: string,
  period: Period
): AsyncThunk {
  return async (dispatch) => {
    const shiftDefs = await getApi().getShiftDefs(teamId, period);
    dispatch(setShiftDefs(shiftDefs));
  };
}

export function markThreadAsRead(chatKey: ChatKey) {
  return {
    type: ActionType.MARK_THREAD_AS_READ,
    payload: chatKey,
  } satisfies AppAction;
}
